import type { OrderWarehouseLine } from '../../../types/orderWarehouseSummary';

/**
 * Physical fulfillment status of the order, from the server quantities only
 * (no finance data): what is issued, what sits in the warehouse and what still
 * has to be delivered. The cost plan („Skąd się bierze marża") is separate.
 */
export type LineFulfillment = 'ISSUED' | 'COVERED' | 'PARTIAL' | 'WAITING' | 'UNKNOWN';
export type OrderFulfillment = 'ISSUED' | 'AVAILABLE' | 'SHORTAGE' | 'UNKNOWN';

const EPSILON = 0.0005;

export interface LineCoverage {
  status: LineFulfillment;
  ordered: number;
  issued: number;
  /** In stock for this order (never more than what is left to issue). */
  available: number;
  /** Left to issue and not in stock: waits for a delivery. */
  missing: number;
  invoiced: number;
}

export function lineCoverage(line: OrderWarehouseLine): LineCoverage {
  const { issuedQuantity, remainingQuantity, availableQuantity, invoicedQuantity } = line;
  const base = { ordered: line.orderedQuantity, invoiced: invoicedQuantity ?? 0 };
  if (issuedQuantity === null || remainingQuantity === null) {
    return { ...base, status: 'UNKNOWN', issued: 0, available: 0, missing: 0 };
  }
  if (remainingQuantity <= EPSILON) {
    return { ...base, status: 'ISSUED', issued: issuedQuantity, available: 0, missing: 0 };
  }
  if (availableQuantity === null) {
    return { ...base, status: 'UNKNOWN', issued: issuedQuantity, available: 0, missing: 0 };
  }
  const available = Math.min(availableQuantity, remainingQuantity);
  const missing = Math.max(0, remainingQuantity - available);
  const status: LineFulfillment = missing <= EPSILON ? 'COVERED' : available <= EPSILON ? 'WAITING' : 'PARTIAL';
  return { ...base, status, issued: issuedQuantity, available, missing };
}

export function orderFulfillment(coverages: LineCoverage[]): OrderFulfillment {
  if (coverages.length === 0) return 'UNKNOWN';
  if (coverages.every(line => line.status === 'ISSUED')) return 'ISSUED';
  if (coverages.some(line => line.status === 'UNKNOWN')) return 'UNKNOWN';
  if (coverages.some(line => line.status === 'WAITING' || line.status === 'PARTIAL')) return 'SHORTAGE';
  return 'AVAILABLE';
}
